/**
 * Бланк додатка декларації з ПДВ (2026-08-05, еталон J05 FormView):
 * офіційний вигляд — шапка додатка (тип/період/платник/ІПН), шрифт з
 * зарубками, чорні рамки; поля редаговані прямо у бланку (жовтий фон).
 *
 * Рендериться зі специфікації бекенда (/vat-declarations/appendix-specs):
 * скалярні поля + таблиці з колонками у порядку XSD. Значення живуть у
 * appendix_data документа; XML генерується З ЦИХ даних (Закон #6 — що на
 * бланку, те у файлі). Підсумкові рядки таблиць рахуються автоматично.
 */
import DatePopover from '@/components/DatePopover';
import type { Organization } from '@/api/catalogs';
import type {
  VatAppendixData, VatAppendixRow, VatAppendixSpec, VatDeclaration,
} from '@/api/vatDeclarations';
import {
  AppendixHeader, B, SERIF, SignaturesBlock, blankBox, nameCell, thBlank,
} from './VatDeclarationBlank';

const fmtTotal = (n: number) =>
  n.toLocaleString('uk-UA', { maximumFractionDigits: 2 });

function CellInput({ type, value, disabled, onChange, print }: {
  type: string;
  value: string | number | null | undefined;
  disabled: boolean;
  onChange: (v: string | null) => void;
  print?: boolean;
}) {
  if (disabled) {
    const shown = type === 'date' && value
      ? String(value).split('-').reverse().join('.')
      : (value ?? '');
    return (
      <span style={{ display: 'block', padding: '2px 4px', fontFamily: SERIF,
        fontSize: '0.8rem', textAlign: ['int', 'dec2'].includes(type) ? 'right' : 'left',
        background: print ? 'transparent' : '#eef1f4', minHeight: '1.1em' }}>
        {shown}
      </span>
    );
  }
  if (type === 'date') {
    return (
      <div style={{ minWidth: 110 }}>
        <DatePopover value={String(value ?? '')} onChange={(v) => onChange(v || null)} />
      </div>
    );
  }
  const numeric = ['int', 'dec2', 'month', 'year'].includes(type);
  return (
    <input
      type={numeric ? 'number' : 'text'}
      step={type === 'dec2' ? '0.01' : '1'}
      value={value ?? ''}
      style={{ width: '100%', minWidth: numeric ? 80 : 120,
        boxSizing: 'border-box', border: 'none', outline: 'none',
        fontFamily: SERIF, fontSize: '0.8rem', padding: '2px 4px',
        textAlign: numeric ? 'right' : 'left', background: 'var(--badge-amber-bg)',
        color: '#111' }}
      onChange={(e) => onChange(e.target.value || null)}
    />
  );
}

export default function VatAppendixTab({
  spec, appLabel, data, doc, org, readOnly, print, onChange,
}: {
  spec: VatAppendixSpec;
  appLabel: string;
  data: VatAppendixData;
  doc: VatDeclaration;
  org: Organization | undefined;
  readOnly: boolean;
  print?: boolean;
  onChange?: (next: VatAppendixData) => void;
}) {
  const fields = (data.fields ?? {}) as Record<string, string | number | null>;
  const change = onChange ?? (() => undefined);

  const setField = (code: string, v: string | null) =>
    change({ ...data, fields: { ...fields, [code]: v } });

  const rowsOf = (dataKey: string): VatAppendixRow[] =>
    (data[dataKey] as VatAppendixRow[] | undefined) ?? [];

  const setRows = (dataKey: string, rows: VatAppendixRow[]) =>
    change({ ...data, [dataKey]: rows });

  return (
    <div style={{ ...blankBox, padding: print ? 0 : undefined }}>
      <div style={{ fontFamily: SERIF, textAlign: 'center', fontWeight: 700,
        fontSize: '0.9rem', padding: '4px 0 6px' }}>
        {spec.title}
      </div>
      <AppendixHeader doc={doc} org={org} appLabel={appLabel} print={print} />

      {!spec.auto && !print && (
        <p style={{ color: 'var(--badge-amber-fg)', fontSize: '0.8rem',
          fontFamily: 'inherit', margin: '0 0 6px' }}>
          ⚠ Додаток заповнюється вручну — перед поданням звірте з друкованою
          формою (підписи рідкісних граф узагальнені).
        </p>
      )}
      {spec.auto && !print && (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem',
          fontFamily: 'inherit', margin: '0 0 6px' }}>
          Заповнено автоматично з ПН періоду («перерахувати з обліку»
          оновлює). Будь-який рядок можна виправити або прибрати — у XML
          піде те, що на бланку.
        </p>
      )}

      {spec.fields.length > 0 && (
        <table className="tch" style={{ marginBottom: 8 }}>
          <colgroup><col /><col style={{ width: 240 }} /></colgroup>
          <tbody>
            {spec.fields.map((f) => (
              <tr key={f.code}>
                <td style={{ ...nameCell }} title={f.code}>{f.label}</td>
                <td style={{ border: B }}>
                  <CellInput type={f.type} value={fields[f.code]}
                    disabled={readOnly} print={print}
                    onChange={(v) => setField(f.code, v)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {spec.tables.map((t) => {
        const rows = rowsOf(t.data_key);
        if (print && rows.length === 0) return null;
        const totals: Record<string, number> = {};
        for (const [totalCode, srcCol] of Object.entries(t.totals)) {
          totals[totalCode] = rows.reduce(
            (s, r) => s + (Number(r[srcCol]) || 0), 0);
        }
        return (
          <div key={t.key} style={{ marginBottom: 10 }}>
            <div style={{ fontFamily: SERIF, fontWeight: 700,
              fontSize: '0.82rem', margin: '6px 0 3px' }}>{t.label}</div>
            <div style={{ overflowX: print ? 'visible' : 'auto' }}>
              <table className="tch" style={{ ...blankBox }}>
                <thead>
                  <tr>
                    <th style={{ ...thBlank, width: 30 }}>№ з/п</th>
                    {t.cols.map((c) => (
                      <th key={c.code} style={{ ...thBlank, width: c.width }} title={c.code}>{c.label}</th>
                    ))}
                    {!readOnly && <th style={{ ...thBlank, width: 28 }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i}>
                      <td style={{ border: B, textAlign: 'center', fontFamily: SERIF, fontSize: '0.8rem' }}>{i + 1}</td>
                      {t.cols.map((c) => (
                        <td key={c.code} style={{ border: B }}>
                          <CellInput type={c.type} value={row[c.code]}
                            disabled={readOnly} print={print}
                            onChange={(v) => {
                              const next = rows.map((r, j) =>
                                j === i ? { ...r, [c.code]: v } : r);
                              setRows(t.data_key, next);
                            }} />
                        </td>
                      ))}
                      {!readOnly && (
                        <td style={{ border: B, textAlign: 'center' }}>
                          <span style={{ cursor: 'pointer', color: '#dc2626' }}
                            title="прибрати рядок"
                            onClick={() => setRows(t.data_key,
                              rows.filter((_, j) => j !== i))}>×</span>
                        </td>
                      )}
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={t.cols.length + 2}
                      style={{ border: B, color: '#94a3b8', fontFamily: SERIF, fontSize: '0.8rem' }}>
                      Немає рядків.
                    </td></tr>
                  )}
                  {Object.keys(t.totals).length > 0 && rows.length > 0 && (
                    <tr style={{ fontWeight: 700 }}>
                      <td style={{ border: B, textAlign: 'center', fontFamily: SERIF, fontSize: '0.8rem' }}>Усього</td>
                      {t.cols.map((c) => {
                        const totalCode = Object.entries(t.totals)
                          .find(([, src]) => src === c.code)?.[0];
                        return (
                          <td key={c.code} style={{ border: B, textAlign: 'right', fontFamily: SERIF, fontSize: '0.8rem' }}
                            title={totalCode}>
                            {totalCode ? fmtTotal(totals[totalCode] ?? 0) : ''}
                          </td>
                        );
                      })}
                      {!readOnly && <td style={{ border: B }}></td>}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {!readOnly && (
              <button style={{ marginTop: 3 }}
                onClick={() => setRows(t.data_key, [...rows, {}])}>
                + рядок
              </button>
            )}
          </div>
        );
      })}

      <SignaturesBlock doc={doc} org={org} print={print} />
    </div>
  );
}
